// The hero paints its poster image first; the video only starts once the page is loaded, so a
// multi-megabyte download never competes with the first paint. Visitors who asked for less
// motion, or for less data, keep the poster.
const EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;

type SaveData = Navigator & { connection?: { saveData?: boolean } };

function play(video: HTMLVideoElement): void {
	const source = video.querySelector<HTMLSourceElement>('source[data-src]');

	if (source) {
		source.src = source.dataset.src ?? '';
		source.removeAttribute('data-src');
		video.load();
	}

	video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
	void video.play().catch(() => undefined);
}

type Idle = Window & { requestIdleCallback?: (run: () => void, options?: { timeout: number }) => void };

function whenQuiet(run: () => void): void {
	const idle = (): void => {
		const schedule = (window as Idle).requestIdleCallback;

		if (schedule) {
			schedule(run, { timeout: 1200 });

			return;
		}

		window.setTimeout(run, 200);
	};

	if (document.readyState === 'complete') {
		idle();

		return;
	}

	window.addEventListener('load', idle, { once: true });
}

export default function initHeroVideo(): void {
	const videos = Array.from(document.querySelectorAll<HTMLVideoElement>('[data-armada-hero-video]'));

	if (!videos.length) {
		return;
	}

	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || (navigator as SaveData).connection?.saveData) {
		return;
	}

	let started = false;

	const start = (): void => {
		if (started) {
			return;
		}

		started = true;
		EVENTS.forEach((event) => window.removeEventListener(event, start));
		videos.forEach(play);
	};

	// A visitor who is already doing something has waited long enough.
	EVENTS.forEach((event) => window.addEventListener(event, start, { passive: true }));
	whenQuiet(start);
}
